import {
  age_filter_id as base_age_filter_id,
  country_filter_id as base_country_filter_id,
} from "../constants";
import { FilterId } from "../types";
import {
  CharacteristicCategory,
  CharacteristicId,
  CharacteristicType,
  ParticipantStatus,
} from "./types";
import type { CountryIso3 } from "@/components/outreach/recruitment/panelPrediction/panelRegistry/countries";

export const UI_ID_PARAM = "iid";
export const USER_INTERVIEWS_URL_PARAMS = [
  "iid",
  "IID",
  "__target_group_id",
  "amp;iid",
  "amp;IID",
  "amp;__target_group_id",
] as const;
// TODO(future): update rates to be in dollars rather than cents
export const BASE_COMPENSATION_CONSUMER_PER_HOUR_CENTS = 2000;
export const BASE_COMPENSATION_PROFESSIONAL_PER_HOUR_CENTS = 4000;
export const VIDEO_MULTIPLIER = 1.2;
export const SCREEN_MULTIPLIER = 1.3;
export const USER_INTERVIEWS_INCENTIVE_CURRENCY = "USD";
export const USER_INTERVIEWS_LAUNCH_TARGET_MULTIPLIER = 3;

// Platform fee we pay User Interviews per completed session, on top of the
// participant incentive. B2B (professional audiences) costs more.
export const SESSION_FEE_CONSUMER_CENTS = 700;
export const SESSION_FEE_PROFESSIONAL_CENTS = 1500;

export const gender_filter_id = "single_select--gender";
export const country_filter_id = "single_select--country";
export const skill_filter_id = "multi_select--skill";
export const occupation_filter_id = "single_select--occupation";
export const age_filter_id = "integer--age";

export const FINAL_STATES: ParticipantStatus[] = [
  "complete",
  "fraudulent",
  "rejected",
  "no_show",
];

export const CharacteristicIdToName: Record<CharacteristicType, string> = {
  "integer--age": "Age",
  "multi_select--browsers": "Browsers",
  "multi_select--children": "Children",
  "multi_select--computer_operating_system": "Computer Operating System",
  "multi_select--employment_status": "Employment Status",
  "multi_select--skill": "Skill",
  "multi_select--smartphone_operating_system": "Smartphone Operating System",
  "multi_select--tablet_operating_system": "Tablet Operating System",
  "multi_select--type_of_income": "Type of Income",
  "single_select--company_size": "Company Size",
  "single_select--computer_with_a_webcam": "Computer with a Webcam",
  "single_select--country": "Country",
  "single_select--gender": "Gender",
  "single_select--home_owner": "Home Owner",
  "single_select--household_income_in_usd": "Household Income in USD",
  "single_select--industry": "Industry",
  "single_select--level_of_education": "Level of Education",
  "single_select--living_situation": "Living Situation",
  "single_select--marital_status": "Marital Status",
  "single_select--occupation": "Occupation",
  "single_select--region": "State (US)",
  "single_select--seniority": "Seniority",
  "single_select--small_business_owner": "Small Business Owner",
  "single_select_with_other--race_ethnicity": "Race/Ethnicity",
  "multi_select--assistive_technologies_and_custom_settings":
    "Assistive Technologies and Custom Settings",
  "multi_select--digital_design_prototyping_tools":
    "Digital Design Prototyping Tools",
  "multi_select--disabilities": "Disabilities",
  "multi_select--food_delivery_services": "Food Delivery Services",
  "multi_select--gig_or_contract_occupation_s": "Gig or Contract Occupation",
  "multi_select--health_and_wellness_apps_platforms":
    "Health and Wellness Apps Platforms",
  "multi_select--health_insurance_type_s": "Health Insurance Type",
  "multi_select--online_banking_financial_management_tools":
    "Online Banking & Financial Management Tools",
  "multi_select--online_learning_educational_platforms":
    "Online Learning Educational Platforms",
  "multi_select--online_marketplace_platforms": "Online Marketplace Platforms",
  "multi_select--online_streaming_services": "Online Streaming Services",
  "multi_select--online_travel_booking_services":
    "Online Travel Booking Services",
  "multi_select--other_language_s_spoken_fluently":
    "Other Language Spoken Fluently",
  "multi_select--pets_owned": "Pets Owned",
  "multi_select--productivity_project_management_software":
    "Productivity Project Management Software",
  "multi_select--smart_home_and_gaming_devices":
    "Smart Home and Gaming Devices",
  "multi_select--social_networking_platforms": "Social Networking Platforms",
  "multi_select--traditional_banks": "Traditional Banks",
  "multi_select--vehicles_currently_owned_or_leased":
    "Vehicles Currently Owned or Leased",
  "single_select--english_language_proficiency_-_speaking":
    "English Language Proficiency Speaking",
  "single_select--military_service_status": "Military Service Status",
  "single_select--neighborhood_type": "Neighborhood Type",
  "single_select--work_setting": "Work Setting",
};

export const CharacteristicToCategory: Record<
  CharacteristicType,
  CharacteristicCategory
> = {
  "integer--age": "Demographics",
  "multi_select--browsers": "Technology and Online Behavior",
  "multi_select--children": "Demographics",
  "multi_select--computer_operating_system": "Technology and Online Behavior",
  "multi_select--employment_status": "Work",
  "multi_select--skill": "Work",
  "multi_select--smartphone_operating_system": "Technology and Online Behavior",
  "multi_select--tablet_operating_system": "Technology and Online Behavior",
  "multi_select--type_of_income": "Finance",
  "single_select--company_size": "Work",
  "single_select--computer_with_a_webcam": "Technology and Online Behavior",
  "single_select--country": "Demographics",
  "single_select--gender": "Demographics",
  "single_select--home_owner": "Demographics",
  "single_select--household_income_in_usd": "Finance",
  "single_select--industry": "Work",
  "single_select--level_of_education": "Demographics",
  "single_select--living_situation": "Demographics",
  "single_select--marital_status": "Demographics",
  "single_select--occupation": "Work",
  "single_select--region": "Demographics",
  "single_select--seniority": "Work",
  "single_select--small_business_owner": "Work",
  "single_select_with_other--race_ethnicity": "Demographics",
  "multi_select--assistive_technologies_and_custom_settings":
    "Technology and Online Behavior",
  "multi_select--digital_design_prototyping_tools":
    "Technology and Online Behavior",
  "multi_select--disabilities": "Demographics",
  "multi_select--food_delivery_services": "Technology and Online Behavior",
  "multi_select--gig_or_contract_occupation_s": "Work",
  "multi_select--health_and_wellness_apps_platforms":
    "Technology and Online Behavior",
  "multi_select--health_insurance_type_s": "Finance",
  "multi_select--online_banking_financial_management_tools": "Finance",
  "multi_select--online_learning_educational_platforms":
    "Technology and Online Behavior",
  "multi_select--online_marketplace_platforms": "Work",
  "multi_select--online_streaming_services": "Technology and Online Behavior",
  "multi_select--online_travel_booking_services":
    "Technology and Online Behavior",
  "multi_select--other_language_s_spoken_fluently": "Demographics",
  "multi_select--pets_owned": "Demographics",
  "multi_select--productivity_project_management_software":
    "Technology and Online Behavior",
  "multi_select--smart_home_and_gaming_devices":
    "Technology and Online Behavior",
  "multi_select--social_networking_platforms": "Technology and Online Behavior",
  "multi_select--traditional_banks": "Finance",
  "multi_select--vehicles_currently_owned_or_leased": "Finance",
  "single_select--english_language_proficiency_-_speaking": "Demographics",
  "single_select--military_service_status": "Demographics",
  "single_select--neighborhood_type": "Demographics",
  "single_select--work_setting": "Work",
};

export const ALL_COUNTRIES = ["AU", "US", "CA", "FR", "DE", "IN", "ZA", "GB"];

export const COMMON_OCCUPATION_CHOICES: Record<string, string> = {
  // Software/Tech
  software_engineer: "Software Engineer",
  software_developer: "Software Developer",
  full_stack_developer: "Full Stack Developer",
  frontend_developer: "Frontend Developer",
  backend_developer: "Backend Developer",
  mobile_developer: "Mobile Developer",
  devops_engineer: "DevOps Engineer",
  data_scientist: "Data Scientist",
  data_engineer: "Data Engineer",
  product_manager: "Product Manager",
  ux_designer: "UX Designer",
  ui_designer: "UI Designer",
  // Marketing
  marketing_specialist: "Marketing Specialist",
  marketing_manager: "Marketing Manager",
  digital_marketer: "Digital Marketer",
  content_marketer: "Content Marketer",
  social_media_manager: "Social Media Manager",
  // Sales
  sales_representative: "Sales Representative",
  sales_manager: "Sales Manager",
  account_manager: "Account Manager",
  // Finance
  financial_analyst: "Financial Analyst",
  accountant: "Accountant",
  financial_advisor: "Financial Advisor",
  // Healthcare
  doctor: "Doctor",
  nurse: "Nurse",
  pharmacist: "Pharmacist",
  healthcare_administrator: "Healthcare Administrator",
  // Education
  teacher: "Teacher",
  professor: "Professor",
  education_administrator: "Education Administrator",
  // Other
  consultant: "Consultant",
  project_manager: "Project Manager",
  operations_manager: "Operations Manager",
  hr_specialist: "HR Specialist",
  lawyer: "Lawyer",
  engineer: "Engineer",
  designer: "Designer",
  analyst: "Analyst",
};

export const USER_INTERVIEWS_COUNTRY_CODE_TO_NAME = {
  AR: "Argentina",
  AT: "Austria",
  AU: "Australia",
  BE: "Belgium",
  BG: "Bulgaria",
  BR: "Brazil",
  CA: "Canada",
  CH: "Switzerland",
  CL: "Chile",
  CZ: "Czechia",
  DE: "Germany",
  DK: "Denmark",
  ES: "Spain",
  FI: "Finland",
  FR: "France",
  GB: "United Kingdom",
  GR: "Greece",
  IE: "Ireland",
  IN: "India",
  IT: "Italy",
  JP: "Japan",
  MX: "Mexico",
  MY: "Malaysia",
  NL: "Netherlands",
  NO: "Norway",
  NZ: "New Zealand",
  PL: "Poland",
  PR: "Puerto Rico",
  PT: "Portugal",
  RO: "Romania",
  SE: "Sweden",
  SG: "Singapore",
  US: "United States",
  ZA: "South Africa",
} as const satisfies Record<string, string>;

export const USER_INTERVIEWS_ISO2_TO_ISO3 = {
  AR: "ARG",
  AT: "AUT",
  AU: "AUS",
  BE: "BEL",
  BG: "BGR",
  BR: "BRA",
  CA: "CAN",
  CH: "CHE",
  CL: "CHL",
  CZ: "CZE",
  DE: "DEU",
  DK: "DNK",
  ES: "ESP",
  FI: "FIN",
  FR: "FRA",
  GB: "GBR",
  GR: "GRC",
  IE: "IRL",
  IN: "IND",
  IT: "ITA",
  JP: "JPN",
  MX: "MEX",
  MY: "MYS",
  NL: "NLD",
  NO: "NOR",
  NZ: "NZL",
  PL: "POL",
  PR: "PRI",
  PT: "PRT",
  RO: "ROU",
  SE: "SWE",
  SG: "SGP",
  US: "USA",
  ZA: "ZAF",
} satisfies Record<
  keyof typeof USER_INTERVIEWS_COUNTRY_CODE_TO_NAME,
  CountryIso3
>;

export const DEFAULT_USER_INTERVIEWS_FILTERS: {
  type: "range" | "select";
  filter_id: FilterId;
  selected_range?: { lower: number; upper: number };
  selected_values?: string[];
  title?: string;
  choices?: Record<string, string>;
  category?: string;
  question?: string | null;
  data_type?: string;
  description?: string | null;
  subcategory?: string | null;
}[] = [
  {
    type: "range" as const,
    filter_id: base_age_filter_id,
    selected_range: { lower: 18, upper: 99 },
  },
  {
    type: "select" as const,
    filter_id: base_country_filter_id,
    title: "Country",
    choices: USER_INTERVIEWS_COUNTRY_CODE_TO_NAME,
    category: "Demographics",
    question: null,
    data_type: "ID",
    description: null,
    subcategory: "Options",
    selected_values: ["US"] as CharacteristicId[],
  },
];
